import * as React from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isNil from 'lodash/isNil';

import BackToTopControl from 'components/back-to-top-control/BackToTopControl';
import BaseSubcampaignPage from './BaseSubcampaignPage';
import SubcampaignOfferEdit from 'modules/subcampaigns/components/subcampaign-offer/SubcampaignOfferEdit';
import SubcampaignOfferView from 'modules/subcampaigns/components/subcampaign-offer/SubcampaignOfferView';
import CampaignSubcampaigns from 'modules/campaigns/components/campaign-subcampaigns/CampaignSubcampaigns';

import { PARTICIPANT_TYPE_SHORTLISTED, SUBCAMPAIGN_STATE_TO_PARTICIPANT_TYPE_MAP } from 'utils/campaignParticipants';
import { SUBCAMPAIGN_OFFER_SORT_VALUES, getEmptyOffer } from 'utils/subcampaign';
import { t } from 'utils/language';
import { canCampaignBeChanged } from 'utils/campaign';
import { BULK_TYPE_CREATE_OFFERS, getSelectedSubcampaigns } from 'utils/bulk-offer';

import type {
  BrandProfile,
  Campaign,
  CampaignOverview,
  Deliverable,
  Subcampaign,
  SubcampaignOffer,
  SubcampaignWithInfluencerSettings
} from 'types';

type StateProps = {
  subcampaign: SubcampaignWithInfluencerSettings,
  brandProfile: BrandProfile,
  campaignOverview: CampaignOverview,
  campaign: Campaign,
  offer: SubcampaignOffer | undefined | null,
  offerDeliverables: Array<Deliverable>,
  isEditMode: boolean,
  isBulkOfferAllowed: boolean,
  isMultipleCreatorsSelected: boolean
};

type OwnProps = {
  params: {
    id: string,
    subcampaignId: string
  }
};

type Props = StateProps & OwnProps;

type State = {
  cachedSubcampaign: Subcampaign | undefined | null,
  cachedCampaign: Campaign | undefined | null,
  showHelperPageStatus: boolean,
  subcampaignOfferSortState: string
};

class BrandSubcampaignOfferPage extends React.Component<Props, State> {
  state = {
    cachedSubcampaign: this.props.subcampaign,
    cachedCampaign: this.props.campaign,
    showHelperPageStatus: false,
    subcampaignOfferSortState: SUBCAMPAIGN_OFFER_SORT_VALUES[0]
  };

  contentRef = React.createRef<HTMLDivElement>();

  componentDidUpdate(): void {
    const { campaign, subcampaign } = this.props;
    const { cachedCampaign, cachedSubcampaign } = this.state;

    if (campaign && campaign !== cachedCampaign || subcampaign && subcampaign !== cachedSubcampaign) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        cachedSubcampaign: subcampaign,
        cachedCampaign: campaign
      });
    }
  }

  hasContent(): boolean {
    const { campaign, subcampaign } = this.props;
    const canEdit = canCampaignBeChanged(campaign);
    const isDraftState = subcampaign?.state === 'draft';
    const canViewOfer = canEdit || !isDraftState;

    return Boolean(subcampaign && subcampaign.creatorProfile && canViewOfer);
  }

  handleSubcampaignOfferSort = (sort: string): void => {
    this.setState({
      subcampaignOfferSortState: sort
    });
  };

  handleShowHelperPageStatus = (status: boolean): void => {
    this.setState({
      showHelperPageStatus: status
    });
  };

  renderHeader = () => {
    const { isBulkOfferAllowed, subcampaign, isEditMode } = this.props;
    const { cachedCampaign, cachedSubcampaign, subcampaignOfferSortState } = this.state;

    if (cachedCampaign && cachedSubcampaign && cachedSubcampaign.state && cachedCampaign.id) {
      return (
        <CampaignSubcampaigns
          campaignId={cachedCampaign.id}
          subcampaign={subcampaign}
          isEditMode={isEditMode}
          subcampaignOfferSortState={subcampaignOfferSortState}
          selectedParticipantsType={
            isBulkOfferAllowed
              ? PARTICIPANT_TYPE_SHORTLISTED
              : SUBCAMPAIGN_STATE_TO_PARTICIPANT_TYPE_MAP[cachedSubcampaign.state]
          }
          onShowHelperPageStatus={this.handleShowHelperPageStatus}
        />
      );
    }

    return null;
  };

  render(): React.ReactNode {
    const {
      subcampaign,
      brandProfile,
      campaignOverview,
      campaign,
      offer,
      offerDeliverables,
      params,
      isEditMode,
      isBulkOfferAllowed,
      isMultipleCreatorsSelected
    } = this.props;

    const { showHelperPageStatus, subcampaignOfferSortState } = this.state;

    return (
      <BaseSubcampaignPage
        params={params}
        subPage="offer-view"
        documentTitle={t('offer_page')}
        renderHeader={this.renderHeader}
      >
        {
          this.hasContent() && !showHelperPageStatus && (
            <div ref={this.contentRef}>
              {
                isEditMode && offer ? (
                  <SubcampaignOfferEdit
                    subcampaign={subcampaign}
                    brandProfile={brandProfile}
                    isBulkOfferAllowed={isBulkOfferAllowed}
                    campaignOverview={campaignOverview}
                    campaign={campaign}
                    offer={offer}
                    offerDeliverables={offerDeliverables}
                    isMultipleCreatorsSelected={isMultipleCreatorsSelected}
                  />
                ) : (
                  <SubcampaignOfferView
                    influencerProfile={subcampaign.creatorProfile}
                    subcampaign={subcampaign}
                    brandProfile={brandProfile}
                    isForBrand
                    campaign={campaign}
                    offerDeliverables={offerDeliverables}
                    sort={subcampaignOfferSortState}
                    onSortChanged={this.handleSubcampaignOfferSort}
                  />
                )
              }
              <BackToTopControl
                isLoading={!offer}
                show
              />
            </div>
          )
        }
      </BaseSubcampaignPage>
    );
  }
}

const mapStateToProps = (
  {
    bulkOffer,
    subcampaign,
    brandProfile,
    campaignOverview,
    campaignBrief,
    offerDeliverables,
    router
  }
): StateProps => {
  let offer: SubcampaignOffer | null = null;
  let offerDeliverablesList = [];
  const isCreateOffer = get(router, 'location.query.bulk') === BULK_TYPE_CREATE_OFFERS;
  const isMultipleCreatorsSelected = getSelectedSubcampaigns(
    bulkOffer.shortlistedSubcampaigns, bulkOffer.selectedIdsMap
  ).length > 1;

  if (subcampaign.data && subcampaign.data.id) {
    if (!isNil(subcampaign.data.updatedOffer)) {
      offer = subcampaign.data.updatedOffer;
    } else if (isMultipleCreatorsSelected || subcampaign.data.offer.isDefault) {
      offer = getEmptyOffer(subcampaign.data.offer);
    } else {
      offer = subcampaign.data.offer;
    }

    offerDeliverablesList = offer
      ? offerDeliverables.deliverablesByOfferId[offer.id]
      : [];
  }

  return {
    subcampaign: subcampaign.data,
    brandProfile: brandProfile.profile,
    campaignOverview: campaignOverview.overview,
    campaign: campaignBrief.brief,
    offer,
    offerDeliverables: offerDeliverablesList,
    isBulkOfferAllowed: bulkOffer.isActive && isCreateOffer,
    isEditMode: /\/edit$/.test(router.location.pathname),
    isMultipleCreatorsSelected
  };
};

export default connect(mapStateToProps)(BrandSubcampaignOfferPage);
